import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { PlacementService } from '../../core/services/placement-service/placement-service.service';
import { TestHistoryItem, Attempt, StartAttemptResponse } from '../../core/models/placement/placement.model';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EnrollCoursesDialogBoxComponent } from '@edtech-ui/student-dashboard-v2/enroll-courses-dialog-box/enroll-courses-dialog-box.component';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { PublicMockTestCatalogItem, PublicMockTestAttemptItem } from '@edtech-ui/core/models/quiz-landing-page/get-all-quiz-link';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Unified shape both placement tests and public quiz-link mock tests are mapped into.
// Field names mirror TestHistoryItem so the same card template can render either source.
type MockTestSource = 'placement' | 'quiz';
export type TestHistoryFilter = 'all' | 'ai-practice' | 'company-specific';

interface UnifiedTestConfig {
	difficulty: string;
	topics: string[];
	num_questions: number;
	company_name: string | null;
	company_logo?: string | null;
	role: string | null;
}

interface UnifiedQuickStartItem {
	source: MockTestSource;
	test_id: string;
	link_id?: string;
	quiz_id?: string;
	test_name: string | null;
	test_type: string;
	test_config: UnifiedTestConfig;
	total_questions: number;
	total_marks: number | null;
	time_limit: number | null;
	is_public: boolean;
	created_by: string | null;
	created_at: string;
	user_attempts_count: number;
	latest_attempt_status: string | null;
	displayTitle: string;
	displayDuration: string;
	cleanTopics: string[];
}

interface UnifiedAttemptItem {
	source: MockTestSource;
	attempt_id: string;
	test_id: string;
	link_id?: string;
	quiz_id?: string;
	test_name: string | null;
	test_type: string;
	difficulty: string | null;
	num_questions: number | null;
	total_marks: number | null;
	status: string;
	start_time: string | null;
	finished_at: string | null;
	time_taken: number | null;
	obtained_marks: number | null;
	score_percentage: number | null;
	total_attempts: number | null;
	retake_permitted: boolean | null;
	created_at: string;
	role_name?: string | null;
}

const SUBMITTED_STATUSES = ['SUBMITTED', 'AUTO_SUBMITTED'];

@Component({
	selector: 'app-placement-dashboard',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, MatMenuModule, RouterModule, FormsModule,
		HasPermissionDirective, MatProgressSpinnerModule
	],
	templateUrl: './placement-dashboard.component.html',
	styleUrl: './placement-dashboard.component.scss'
})
export class PlacementDashboardComponent implements OnInit, OnDestroy {
	activeTab: string = 'mock-test';
	quickStartTests: UnifiedQuickStartItem[] = [];
	filteredQuickStartTests: UnifiedQuickStartItem[] = [];
	attempts: UnifiedAttemptItem[] = [];
	filteredAttempts: UnifiedAttemptItem[] = [];
	searchQuery: string = '';
	selectedFilter: string | null = null;
	quickStartSearchQuery: string = '';
	quickStartSelectedFilter: string | null = null;
	PermissionForStudents = PermissionForStudents;

	filterOptions = [
		{ label: 'Aptitude', value: 'APTITUDE' },
		{ label: 'Logical Reasoning', value: 'LOGICAL_REASONING' },
		{ label: 'Verbal Ability', value: 'VERBAL_ABILITY' },
		{ label: 'Programming', value: 'PROGRAMMING' },
		{ label: 'Mixed Test', value: 'MIXED_TEST' },
		{ label: 'Company Specific', value: 'COMPANY_SPECIFIC' },
		{ label: 'Resume Based', value: 'RESUME_BASED' }
	];

	isLoading: boolean = false;
	activeStartingTestId: string | null = null;

	// Quick Start tab: two independently-paginated sources merged and shown
	// page-by-page via "Load More" (a true single merged pager isn't possible
	// without a backend aggregator, which was explicitly out of scope).
	quickStartPage = 1;
	readonly quickStartPageSize = 12;
	isLoadingMoreQuickStart = false;
	hasMorePlacementQuickStart = true;
	hasMoreQuizQuickStart = true;
	hasMoreQuickStart = true;

	// Test History tab: same merge pattern.
	historyPage = 1;
	readonly historyPageSize = 12;
	isLoadingMoreHistory = false;
	hasMorePlacementHistory = true;
	hasMoreQuizHistory = true;
	hasMoreHistory = true;

	private destroy$ = new Subject<void>();
	private tabChange$ = new Subject<void>();

	constructor(
		private placementService: PlacementService,
		private router: Router,
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private dialog: MatDialog,
		private quizService: QuizManagementService,
	) { }

	ngOnInit(): void {
		this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
			this.tabChange$.next();
			this.activeTab = params['tab'] || 'mock-test';
			if (this.activeTab === 'history') {
				this.fetchAttempts(true);
			} else {
				this.fetchQuickStartTests(true);
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ========================================
	// Quick Start tab ("All Mock Tests")
	// ========================================

	quickStartSourceFilter: TestHistoryFilter = 'all';

	getQuickStartSourceFilterLabel(): string {
		const opt = this.historyFilterOptions.find(o => o.value === this.quickStartSourceFilter);
		return opt ? opt.label : 'All';
	}

	setQuickStartSourceFilter(filter: TestHistoryFilter) {
		if (this.quickStartSourceFilter === filter) return;
		this.quickStartSourceFilter = filter;
		if (filter !== 'company-specific') {
			this.selectedRoleName = null;
			this.selectedTestCategory = 'All';
			this.roleSearchQuery = '';
			this.isRoleDropdownOpen = false;
		}
		this.fetchQuickStartTests(true);
	}

	clearQuickStartSearch() {
		this.quickStartSearchQuery = '';
		this.applyQuickStartFilters();
	}

	fetchQuickStartTests(reset: boolean = false) {
		if (reset) {
			this.isLoading = true;
			this.quickStartPage = 1;
			this.quickStartTests = [];
			this.filteredQuickStartTests = [];
			this.hasMorePlacementQuickStart = true;
			this.hasMoreQuizQuickStart = true;
			this.hasMoreQuickStart = true;
		} else {
			this.isLoadingMoreQuickStart = true;
		}

		const shouldFetchPlacement =
			(this.quickStartSourceFilter === 'all' || this.quickStartSourceFilter === 'ai-practice') &&
			this.hasMorePlacementQuickStart;

		const shouldFetchQuiz =
			(this.quickStartSourceFilter === 'all' || this.quickStartSourceFilter === 'company-specific') &&
			this.hasMoreQuizQuickStart;

		const roleParam = (this.quickStartSourceFilter === 'company-specific' && this.selectedRoleName)
			? this.selectedRoleName
			: undefined;

		const categoryParam = (this.quickStartSourceFilter === 'company-specific' && this.selectedTestCategory && this.selectedTestCategory !== 'All')
			? this.selectedTestCategory
			: undefined;

		const placement$ = shouldFetchPlacement
			? this.placementService.getTestHistory(this.quickStartPage, this.quickStartPageSize, 'public_tests').pipe(catchError(() => of(null)))
			: of(null);
		const quiz$ = shouldFetchQuiz
			? this.quizService.getPublicMockTestCatalog(
				this.quickStartPage,
				this.quickStartPageSize,
				undefined,
				categoryParam,
				roleParam
			).pipe(catchError(() => of(null)))
			: of(null);

		forkJoin({ placement: placement$, quiz: quiz$ })
			.pipe(takeUntil(this.tabChange$), takeUntil(this.destroy$))
			.subscribe(({ placement, quiz }) => {
				this.isLoading = false;
				this.isLoadingMoreQuickStart = false;

				const placementItems = (placement?.Success && placement.Data?.data)
					? placement.Data.data.map(t => this.mapPlacementTestToView(t))
					: [];
				const quizItems = (quiz?.Success && quiz.Data?.data)
					? quiz.Data.data.map(t => this.mapQuizCatalogToView(t))
					: [];

				if (quiz?.Success && quiz.Data?.data) {
					for (const item of quiz.Data.data) {
						const r = item.test_config?.role;
						if (r && typeof r === 'string' && !this.roles.includes(r)) {
							this.roles.push(r);
						}
					}
				}

				const placementCount = (placement?.Success && placement.Data?.data) ? placement.Data.data.length : 0;
				const quizCount = (quiz?.Success && quiz.Data?.data) ? quiz.Data.data.length : 0;

				const placementTotalPages = placement?.Success && placement.Data?.total_pages ? placement.Data.total_pages : 1;
				const quizTotalPages = quiz?.Success && quiz.Data?.total_pages ? quiz.Data.total_pages : 1;

				const hasMorePlacement = placementCount >= this.quickStartPageSize && this.quickStartPage < placementTotalPages;
				const hasMoreQuiz = quizCount >= this.quickStartPageSize && this.quickStartPage < quizTotalPages;

				if (this.quickStartSourceFilter === 'all') {
					this.hasMorePlacementQuickStart = hasMorePlacement;
					this.hasMoreQuizQuickStart = hasMoreQuiz;
					this.hasMoreQuickStart = this.hasMorePlacementQuickStart || this.hasMoreQuizQuickStart;
				} else if (this.quickStartSourceFilter === 'ai-practice') {
					this.hasMorePlacementQuickStart = hasMorePlacement;
					this.hasMoreQuizQuickStart = false;
					this.hasMoreQuickStart = this.hasMorePlacementQuickStart;
				} else if (this.quickStartSourceFilter === 'company-specific') {
					this.hasMorePlacementQuickStart = false;
					this.hasMoreQuizQuickStart = hasMoreQuiz;
					this.hasMoreQuickStart = this.hasMoreQuizQuickStart;
				}

				// Deduplicate tests by test_id
				const existingMap = new Map<string, UnifiedQuickStartItem>();
				if (!reset) {
					for (const item of this.quickStartTests) {
						const key = item.test_id || `${item.source}_${item.displayTitle}`;
						existingMap.set(key, item);
					}
				}

				for (const item of [...placementItems, ...quizItems]) {
					const key = item.test_id || `${item.source}_${item.displayTitle}`;
					if (!existingMap.has(key)) {
						existingMap.set(key, item);
					}
				}

				this.quickStartTests = Array.from(existingMap.values())
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				this.applyQuickStartFilters();

				if (!placement?.Success && placement !== null && shouldFetchPlacement) {
					this.toastr.error('Failed to load some placement tests.');
				}
			});
	}

	loadMoreQuickStart() {
		if (!this.hasMoreQuickStart || this.isLoadingMoreQuickStart) return;
		this.quickStartPage++;
		this.fetchQuickStartTests(false);
	}

	private mapPlacementTestToView(test: TestHistoryItem): UnifiedQuickStartItem {
		return {
			source: 'placement',
			test_id: test.test_id,
			test_name: test.test_name ?? null,
			test_type: test.test_type,
			test_config: {
				...test.test_config,
				company_logo: test.test_config?.company_logo || null
			},
			total_questions: test.total_questions,
			total_marks: test.total_marks,
			time_limit: test.time_limit,
			is_public: test.is_public,
			created_by: test.created_by,
			created_at: test.created_at,
			user_attempts_count: test.user_attempts_count,
			latest_attempt_status: test.latest_attempt_status,
			displayTitle: test.test_name ? test.test_name : 'Test',
			displayDuration: this.formatDuration(test.time_limit),
			cleanTopics: this.formatTopics(test.test_config.topics)
		};
	}

	private mapQuizCatalogToView(test: PublicMockTestCatalogItem): UnifiedQuickStartItem {
		return {
			source: 'quiz',
			test_id: test.test_id,
			link_id: test.link_id,
			quiz_id: test.quiz_id,
			test_name: test.test_name,
			test_type: test.test_type || 'General',
			test_config: {
				...test.test_config,
				company_logo: test.test_config?.company_logo || test.company_logo || null
			},
			total_questions: test.total_questions,
			total_marks: test.total_marks,
			time_limit: test.time_limit,
			is_public: test.is_public,
			created_by: test.created_by,
			created_at: test.created_at || new Date().toISOString(),
			user_attempts_count: test.user_attempts_count,
			latest_attempt_status: test.latest_attempt_status,
			displayTitle: test.test_name ? test.test_name : 'Mock Test',
			displayDuration: this.formatDuration(test.time_limit),
			cleanTopics: this.formatTopics(test.test_config.topics)
		};
	}

	onCompanyLogoError(test: UnifiedQuickStartItem) {
		if (test?.test_config) {
			test.test_config.company_logo = null;
		}
	}

	applyQuickStartFilters() {
		const q = this.quickStartSearchQuery ? this.quickStartSearchQuery.trim().toLowerCase() : '';

		this.filteredQuickStartTests = this.quickStartTests.filter(test => {
			if (this.quickStartSourceFilter === 'ai-practice' && test.source !== 'placement') {
				return false;
			}
			if (this.quickStartSourceFilter === 'company-specific' && test.source !== 'quiz') {
				return false;
			}

			if (this.quickStartSelectedFilter) {
				const isCompanySpecificFilter = this.quickStartSelectedFilter === 'COMPANY_SPECIFIC';
				const matchCategory = test.test_type === this.quickStartSelectedFilter ||
					(isCompanySpecificFilter && (test.source === 'quiz' || test.test_type === 'COMPANY_SPECIFIC' || !!test.test_config?.company_name));
				if (!matchCategory) {
					return false;
				}
			}

			if (!q) {
				return true;
			}

			const titleMatch = !!(test.displayTitle && test.displayTitle.toLowerCase().includes(q));
			const typeMatch = !!(test.test_type && test.test_type.toLowerCase().includes(q));
			const companyMatch = !!(test.test_config?.company_name && test.test_config.company_name.toLowerCase().includes(q));
			const topicsMatch = !!(test.cleanTopics && test.cleanTopics.some(topic => topic.toLowerCase().includes(q)));

			return titleMatch || typeMatch || companyMatch || topicsMatch;
		});
	}

	setQuickStartFilter(filter: string | null) {
		this.quickStartSelectedFilter = this.quickStartSelectedFilter === filter ? null : filter;
		this.applyQuickStartFilters();
	}

	// ========================================
	// Test History tab
	// ========================================

	historyFilter: TestHistoryFilter = 'all';

	readonly historyFilterOptions: { label: string; value: TestHistoryFilter }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'AI Practice Mock Test', value: 'ai-practice' },
		{ label: 'Company Specific Mock Test', value: 'company-specific' }
	];

	readonly defaultRoles: string[] = [
		'Software Engineer'
	];

	roles: string[] = [...this.defaultRoles];
	selectedRoleName: string | null = null;
	roleSearchQuery: string = '';
	isRoleDropdownOpen: boolean = false;

	readonly testCategoryOptions: string[] = ['All', 'Technical', 'Management', 'General'];
	selectedTestCategory: string = 'All';

	fetchAttempts(reset: boolean = false) {
		if (reset) {
			this.isLoading = true;
			this.historyPage = 1;
			this.attempts = [];
			this.filteredAttempts = [];
			this.hasMorePlacementHistory = true;
			this.hasMoreQuizHistory = true;
			this.hasMoreHistory = true;
		} else {
			this.isLoadingMoreHistory = true;
		}

		const shouldFetchPlacement =
			(this.historyFilter === 'all' || this.historyFilter === 'ai-practice') &&
			this.hasMorePlacementHistory;

		const shouldFetchQuiz =
			(this.historyFilter === 'all' || this.historyFilter === 'company-specific') &&
			this.hasMoreQuizHistory;

		const roleParam = (this.historyFilter === 'company-specific' && this.selectedRoleName)
			? this.selectedRoleName
			: undefined;

		const categoryParam = (this.historyFilter === 'company-specific' && this.selectedTestCategory && this.selectedTestCategory !== 'All')
			? this.selectedTestCategory
			: undefined;

		const placement$ = shouldFetchPlacement
			? this.placementService.getAttempts(this.historyPage, this.historyPageSize, this.selectedFilter, null, 'created_at', 'desc').pipe(catchError(() => of(null)))
			: of(null);

		const quiz$ = shouldFetchQuiz
			? this.quizService.getPublicMockTestAttempts(
				this.historyPage,
				this.historyPageSize,
				'start_time',
				'desc',
				roleParam,
				categoryParam
			).pipe(catchError(() => of(null)))
			: of(null);

		forkJoin({ placement: placement$, quiz: quiz$ })
			.pipe(takeUntil(this.tabChange$), takeUntil(this.destroy$))
			.subscribe({
				next: ({ placement, quiz }) => {
					this.isLoading = false;
					this.isLoadingMoreHistory = false;

					const placementItems = (placement?.Success && placement.Data?.data)
						? placement.Data.data.map(a => this.mapPlacementAttemptToView(a))
						: [];
					const quizItems = (quiz?.Success && quiz.Data?.data)
						? quiz.Data.data.map(a => this.mapQuizAttemptToView(a))
						: [];

					const placementCount = (placement?.Success && placement.Data?.data) ? placement.Data.data.length : 0;
					const quizCount = (quiz?.Success && quiz.Data?.data) ? quiz.Data.data.length : 0;

					const placementTotalPages = placement?.Success && placement.Data?.total_pages ? placement.Data.total_pages : 1;
					const quizTotalPages = quiz?.Success && quiz.Data?.total_pages ? quiz.Data.total_pages : 1;

					const hasMorePlacement = placementCount >= this.historyPageSize && this.historyPage < placementTotalPages;
					const hasMoreQuiz = quizCount >= this.historyPageSize && this.historyPage < quizTotalPages;

					if (this.historyFilter === 'all') {
						this.hasMorePlacementHistory = hasMorePlacement;
						this.hasMoreQuizHistory = hasMoreQuiz;
						this.hasMoreHistory = this.hasMorePlacementHistory || this.hasMoreQuizHistory;
					} else if (this.historyFilter === 'ai-practice') {
						this.hasMorePlacementHistory = hasMorePlacement;
						this.hasMoreQuizHistory = false;
						this.hasMoreHistory = this.hasMorePlacementHistory;
					} else if (this.historyFilter === 'company-specific') {
						this.hasMorePlacementHistory = false;
						this.hasMoreQuizHistory = hasMoreQuiz;
						this.hasMoreHistory = this.hasMoreQuizHistory;
					}

					// Merge and deduplicate records if the same attempt exists in both responses
					const existingMap = new Map<string, UnifiedAttemptItem>();
					if (!reset) {
						for (const item of this.attempts) {
							const key = item.attempt_id || `${item.source}_${item.test_id}_${item.created_at}`;
							existingMap.set(key, item);
						}
					}

					for (const item of [...placementItems, ...quizItems]) {
						const key = item.attempt_id || `${item.source}_${item.test_id}_${item.created_at}`;
						if (!existingMap.has(key)) {
							existingMap.set(key, item);
						}
					}

					this.attempts = Array.from(existingMap.values())
						.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

					this.applyFilters();
				},
				error: () => {
					this.isLoading = false;
					this.isLoadingMoreHistory = false;
					this.applyFilters();
				}
			});
	}

	loadMoreHistory() {
		if (!this.hasMoreHistory || this.isLoadingMoreHistory) return;
		this.historyPage++;
		this.fetchAttempts(false);
	}

	private mapPlacementAttemptToView(attempt: Attempt): UnifiedAttemptItem {
		return {
			source: 'placement',
			attempt_id: attempt.attempt_id,
			test_id: attempt.test_id,
			test_name: attempt.test_name ?? null,
			test_type: attempt.test_type,
			difficulty: attempt.difficulty,
			num_questions: attempt.num_questions,
			total_marks: attempt.total_marks,
			status: attempt.status,
			start_time: attempt.start_time,
			finished_at: attempt.finished_at,
			time_taken: attempt.time_taken,
			obtained_marks: attempt.obtained_marks,
			score_percentage: attempt.score_percentage,
			total_attempts: attempt.total_attempts,
			retake_permitted: attempt.retake_permitted,
			created_at: attempt.created_at
		};
	}

	private mapQuizAttemptToView(attempt: PublicMockTestAttemptItem): UnifiedAttemptItem {
		return {
			source: 'quiz',
			attempt_id: attempt.attempt_id,
			test_id: attempt.test_id,
			link_id: attempt.link_id,
			quiz_id: attempt.quiz_id,
			test_name: attempt.test_name,
			test_type: attempt.test_type || 'General',
			difficulty: null,
			num_questions: attempt.num_questions,
			total_marks: attempt.total_marks,
			status: attempt.status || 'SUBMITTED',
			start_time: attempt.start_time,
			finished_at: attempt.finished_at,
			time_taken: attempt.time_taken,
			obtained_marks: attempt.obtained_marks,
			score_percentage: attempt.score_percentage,
			total_attempts: attempt.total_attempts,
			retake_permitted: attempt.retake_permitted,
			created_at: attempt.created_at || attempt.start_time || new Date().toISOString(),
			role_name: (attempt as any).role_name || null
		};
	}

	applyFilters() {
		const q = this.searchQuery ? this.searchQuery.trim().toLowerCase() : '';

		this.filteredAttempts = this.attempts.filter(attempt => {
			if (this.historyFilter === 'ai-practice' && attempt.source !== 'placement') {
				return false;
			}
			if (this.historyFilter === 'company-specific' && attempt.source !== 'quiz') {
				return false;
			}

			if (this.selectedFilter) {
				const isCompanySpecificFilter = this.selectedFilter === 'COMPANY_SPECIFIC';
				const matchCategory = attempt.test_type === this.selectedFilter ||
					(isCompanySpecificFilter && (attempt.source === 'quiz' || attempt.test_type === 'COMPANY_SPECIFIC'));

				if (!matchCategory) {
					return false;
				}
			}

			if (!q) {
				return true;
			}

			const nameMatch = !!(attempt.test_name && attempt.test_name.toLowerCase().includes(q));
			const typeMatch = !!(attempt.test_type && attempt.test_type.toLowerCase().includes(q));
			const formattedTypeMatch = !!(attempt.test_type && this.formatTestType(attempt.test_type).toLowerCase().includes(q));
			return nameMatch || typeMatch || formattedTypeMatch;
		});
	}

	getHistoryFilterLabel(): string {
		const opt = this.historyFilterOptions.find(o => o.value === this.historyFilter);
		return opt ? opt.label : 'All';
	}

	setHistoryFilter(filter: TestHistoryFilter) {
		if (this.historyFilter === filter) return;
		this.historyFilter = filter;
		if (filter !== 'company-specific') {
			this.selectedRoleName = null;
			this.selectedTestCategory = 'All';
			this.roleSearchQuery = '';
			this.isRoleDropdownOpen = false;
		}
		this.fetchAttempts(true);
	}

	clearSearch() {
		this.searchQuery = '';
		this.applyFilters();
	}

	setFilter(filter: string | null) {
		this.selectedFilter = this.selectedFilter === filter ? null : filter;
		this.fetchAttempts(true);
	}

	onFilterChange() {
		if (this.activeTab === 'mock-test') {
			this.quickStartPage = 1;
			this.fetchQuickStartTests(true);
		} else {
			this.historyPage = 1;
			this.fetchAttempts(true);
		}
	}

	get filteredRoles(): string[] {
		const q = this.roleSearchQuery ? this.roleSearchQuery.trim().toLowerCase() : '';
		if (!q) return this.roles;
		return this.roles.filter(r => r.toLowerCase().includes(q));
	}

	toggleRoleDropdown(event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
	}

	onSelectRole(role: string) {
		const newRole = role && role.trim() ? role.trim() : null;
		if (this.selectedRoleName === newRole) {
			this.isRoleDropdownOpen = false;
			return;
		}
		this.selectedRoleName = newRole;
		if (newRole && !this.roles.includes(newRole)) {
			this.roles.unshift(newRole);
		}
		this.roleSearchQuery = '';
		this.isRoleDropdownOpen = false;
		this.onFilterChange();
	}

	clearSelectedRole(event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		if (!this.selectedRoleName) return;
		this.selectedRoleName = null;
		this.roleSearchQuery = '';
		this.onFilterChange();
	}

	onSelectCategory(category: string) {
		if (this.selectedTestCategory === category) return;
		this.selectedTestCategory = category;
		this.onFilterChange();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (!target.closest('.role-dropdown-container')) {
			this.isRoleDropdownOpen = false;
		}
	}

	// ========================================
	// Shared tab / navigation plumbing
	// ========================================

	selectTab(tab: string) {
		this.activeTab = tab;
		this.selectedRoleName = null;
		this.selectedTestCategory = 'All';
		this.roleSearchQuery = '';
		this.isRoleDropdownOpen = false;
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { tab: tab },
			queryParamsHandling: 'merge',
		});
	}

	private goToMockTestAssessment(linkId: string) {
		this.router.navigate(['/my-assessment', linkId], { queryParams: { isStudentDashboard: 'true', fromMockTest: 'true' } });
	}

	startTest(test: UnifiedQuickStartItem) {
		if (this.activeStartingTestId) return;

		if (test.source === 'quiz') {
			if (!test.link_id) return;
			this.goToMockTestAssessment(test.link_id);
			return;
		}

		this.activeStartingTestId = test.test_id;
		this.placementService.startAttempt(test.test_id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: StartAttemptResponse) => {
					this.activeStartingTestId = null;
					if (res.Success && res.Data) {
						this.router.navigate(['/student-dashboard/placement-test'], {
							state: {
								testId: test.test_id,
								testTitle: test.displayTitle,
								questions: res.Data.questions,
								durationMinutes: test.time_limit ? Math.floor(test.time_limit / 60) : 30,
								attemptId: res.Data.attempt_id
							}
						});
						return;
					}

					if (res.Status === 400 && !res.Success) {
						this.toastr.warning(res.Message || 'You have reached your attempt limit', 'Attempt Limit', {
							timeOut: 5000,
							progressBar: true
						});
						return;
					}

					if (res.Status === 402 && !res.Success && res.Data) {
						const popupData = {
							title: res.Data?.title,
							message: res.Data?.message
						};

						const dialogRef = this.openAttemptMockLimitDialogBox(popupData);
						this.router.events.subscribe(() => {
							dialogRef.close();
						})
						return;
					}
				},
				error: (err) => {
					this.activeStartingTestId = null;

					if (err?.status === 400 && err?.error?.Message) {
						this.toastr.warning(err.error.Message, 'Attempt Limit', {
							timeOut: 5000,
							progressBar: true
						});
					} else {
						this.toastr.error('Could not start test. Please try again.');
					}
				}
			});
	}

	viewResult(attempt: UnifiedAttemptItem) {
		if (attempt.source === 'quiz') {
			this.viewReport(attempt.attempt_id);
			return;
		}

		this.placementService.getAttemptsByTestId(attempt.test_id, 1, 10)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					if (res.Success && res.Data) {
						const selectedAttemptData = res.Data.attempts.find(a => a.attempt_id === attempt.attempt_id);

						if (selectedAttemptData) {
							this.router.navigate(['/student-dashboard/placement-test-result'], {
								state: {
									allAttemptsData: res.Data,
									selectedAttemptId: attempt.attempt_id
								}
							});
						} else {
							this.toastr.error('Attempt not found in response');
						}
					} else {
						this.toastr.error('Failed to load result: ' + res.Message);
					}
				},
				error: (err) => {
					this.toastr.error('Failed to connect to server.');
				}
			});
	}


	viewReport(attemptId?: string) {
		this.router.navigate(['/assesment-report', attemptId], { queryParams: { fromMockTest: 'true' } });
	}

	formatDate(dateStr: string): string {
		const date = new Date(dateStr);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString('default', { month: 'short' });
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	}

	resumeTest(item: UnifiedQuickStartItem | UnifiedAttemptItem) {
		if (item.source === 'quiz') {
			if (!item.link_id) return;
			this.goToMockTestAssessment(item.link_id);
			return;
		}

		if ('attempt_id' in item && item.attempt_id) {
			this.navigateToResume(item.attempt_id, item.test_id, item.test_name || this.formatTestType(item.test_type));
		} else {
			this.placementService.getAttemptsByTestId(item.test_id, 1, 100)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (res) => {
						if (res.Success && res.Data && res.Data.attempts) {
							const inProgressAttempt = res.Data.attempts.find(a => a.status === 'IN_PROGRESS');
							if (inProgressAttempt) {
								this.navigateToResume(inProgressAttempt.attempt_id, item.test_id, item.test_name || this.formatTestType(item.test_type));
							} else {
								this.toastr.error('No active attempt found to resume.');
							}
						} else {
							this.toastr.error('Failed to fetch attempts to resume.');
						}
					},
					error: (err) => {
						console.error('Failed to fetch attempts for resume:', err);
						this.toastr.error('Could not resume test. Please try again.');
					}
				});
		}
	}

	private navigateToResume(attemptId: string, testId: string, testTitle: string) {
		this.placementService.getAttemptById(attemptId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					if (res.Success && res.Data) {
						this.router.navigate(['/student-dashboard/placement-test'], {
							state: {
								testId: testId,
								attemptId: attemptId,
								testTitle: testTitle,
								resumeData: res.Data
							}
						});
					} else {
						this.toastr.error('Failed to resume test: ' + res.Message);
					}
				},
				error: (err) => {
					this.toastr.error('Could not resume test. Please try again.');
				}
			});
	}

	retakeTest(item: UnifiedQuickStartItem | UnifiedAttemptItem) {
		if (item.source === 'quiz') {
			if (!item.link_id) return;
			this.goToMockTestAssessment(item.link_id);
			return;
		}

		if (this.activeStartingTestId) return;

		this.activeStartingTestId = item.test_id;
		this.placementService.startAttempt(item.test_id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: StartAttemptResponse) => {
					this.activeStartingTestId = null;
					if (res.Success && res.Data) {
						this.router.navigate(['/student-dashboard/placement-test'], {
							state: {
								testId: item.test_id,
								testTitle: item.test_name || this.formatTestType(item.test_type),
								questions: res.Data.questions,
								durationMinutes: 30,
								attemptId: res.Data.attempt_id
							}
						});
						return;
					}

					if (res.Status === 400 && !res.Success) {
						this.toastr.warning(res.Message || 'You have reached your attempt limit', 'Attempt Limit', {
							timeOut: 5000,
							progressBar: true
						});
						return;
					}

					if (res.Status === 402 && !res.Success && res.Data) {
						const popupData = {
							title: res.Data?.title,
							message: res.Data?.message
						};

						const dialogRef = this.openAttemptMockLimitDialogBox(popupData);
						this.router.events.subscribe(() => {
							dialogRef.close();
						});
						return;
					}
				},
				error: (err) => {
					this.activeStartingTestId = null;

					if (err?.status === 400 && err?.error?.Message) {
						this.toastr.warning(err.error.Message, 'Attempt Limit', {
							timeOut: 5000,
							progressBar: true
						});
					} else {
						this.toastr.error('Could not retake test. Please try again.');
					}
				}
			});
	}

	formatDuration(seconds: number | null): string {
		if (seconds == null || seconds <= 0) return '0 min';
		const mins = Math.floor(seconds / 60);
		if (mins < 60) {
			return `${mins} min`;
		}
		const hours = Math.floor(mins / 60);
		const remainingMins = mins % 60;
		if (remainingMins === 0) {
			return `${hours} hr`;
		}
		return `${hours} hr ${remainingMins} min`;
	}

	formatTestType(type: string): string {
		return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
	}

	private formatTopics(topics: string[]): string[] {
		if (!topics) return [];

		const processedTopics = topics.map(topic => {
			const parts = topic.split(' - ');
			return parts.length > 1 ? parts[1].trim() : parts[0].trim();
		});

		return [...new Set(processedTopics)];
	}

	isSubmittedStatus(status: string | null): boolean {
		return !!status && SUBMITTED_STATUSES.includes(status);
	}

	getDisplayTime(attempt: UnifiedAttemptItem): string {
		const timeTaken = attempt.time_taken || 0;

		if (timeTaken === 0) return '0s';

		const hours = Math.floor(timeTaken / 3600);
		const mins = Math.floor((timeTaken % 3600) / 60);
		const secs = timeTaken % 60;

		const parts = [];
		if (hours > 0) parts.push(`${hours}h`);
		if (mins > 0) parts.push(`${mins}m`);
		if (secs > 0) parts.push(`${secs}s`);

		return parts.join(' ');
	}

	getFilterLabel(value: string | null): string {
		const found = this.filterOptions.find(o => o.value === value);
		return found ? found.label : 'All Filters';
	}

	getTestTypeIcon(testType: string): string {
		switch (testType) {
			case 'APTITUDE': return 'psychology';
			case 'LOGICAL_REASONING': return 'extension';
			case 'VERBAL_ABILITY': return 'menu_book';
			case 'PROGRAMMING': return 'code';
			case 'MIXED_TEST': return 'layers';
			case 'COMPANY_SPECIFIC': return 'business';
			case 'RESUME_BASED': return 'description';
			case 'Technical': return 'code';
			case 'Management': return 'business_center';
			case 'General': return 'quiz';
			default: return 'article';
		}
	}

	getTestTypeLabel(testType: string): string {
		switch (testType) {
			case 'APTITUDE': return 'Aptitude';
			case 'LOGICAL_REASONING': return 'Logical';
			case 'VERBAL_ABILITY': return 'Verbal';
			case 'PROGRAMMING': return 'Programming';
			case 'MIXED_TEST': return 'Mixed';
			case 'COMPANY_SPECIFIC': return 'Company';
			case 'RESUME_BASED': return 'Resume';
			default: return this.formatTestType(testType);
		}
	}

	getTestTypeClass(testType: string): string {
		switch (testType) {
			case 'APTITUDE': return 'badge-purple';
			case 'LOGICAL_REASONING': return 'badge-blue';
			case 'VERBAL_ABILITY': return 'badge-teal';
			case 'PROGRAMMING': return 'badge-yellow';
			case 'MIXED_TEST': return 'badge-orange';
			case 'COMPANY_SPECIFIC': return 'badge-indigo';
			case 'RESUME_BASED': return 'badge-pink';
			case 'Technical': return 'badge-yellow';
			case 'Management': return 'badge-indigo';
			case 'General': return 'badge-blue';
			default: return 'badge-default';
		}
	}

	openAttemptMockLimitDialogBox(data: { title: string, message: string }): MatDialogRef<EnrollCoursesDialogBoxComponent> {
		return this.dialog.open(EnrollCoursesDialogBoxComponent, {
			data
		})
	}
}